import React from 'react';
import { Badge, Col, Label, Link, Row, Text } from '@zg-rentals/multibrand-ui/lib';
import StringConstants from '@common/constants/StringConstants';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import TransactionConstants from '@common/constants/TransactionConstants';
import HelpToolTip from '@rental-manager/pages/Onboarding/HelpToolTip';
import { ITransactionModel, RentalProtectionCostInterface } from '@rental-manager/models/Types';
import ExpansionPanel from '@zg-rentals/multibrand-ui/lib/ExpansionPanel';

type Props = {
  transaction: ITransactionModel;
  rProTransaction: RentalProtectionCostInterface;
  listingAlias: string;
};

const RentalProtectionPremiumRow: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  const [expandedRentalProtectionPremiumSection, setExpandedRentalProtectionPremiumSection] =
    React.useState<boolean>(false);

  const toggleRentalProtectionPremium = (): void => {
    setExpandedRentalProtectionPremiumSection(!expandedRentalProtectionPremiumSection);
  };

  const renderExpansionHeader = (): React.ReactNode => {
    const { landlordStatus, ledgerGroup } = props.transaction;
    const { premiumMonthTitle, amount } = props.rProTransaction;

    const showReturnedBadgeForPastDue =
      ledgerGroup === TransactionConstants.LEDGER_GROUP.PAST_DUE &&
      (landlordStatus === TransactionConstants.STATUS.REFUNDED ||
        landlordStatus === TransactionConstants.STATUS.DISPUTED);

    return (
      <Row className="pbn">
        <Col xs={8} sm={8} md={10} lg={10} xl={10} xxl={10} className="paymentDetailsRow-Mobile">
          <Row className="pbn">
            <Col>
              <Text className="fs-exclude" emphasize>
                {premiumMonthTitle || 'Rental Protection'}
              </Text>
            </Col>
          </Row>
        </Col>
        <Col xs={4} sm={4} md={2} lg={2} xl={2} xxl={2} className="paymentDetailsRow-Mobile">
          <Row className="pbn">
            <Col className="Dashboard-Price-Badge-Padding txtR">
              <Text type="body" className="Dashboard-Price fs-exclude">
                {centsToDollars(amount)}
              </Text>
              {landlordStatus === TransactionConstants.STATUS.PENDING && (
                <Badge className="txtR fs-exclude" label="pending" type="info" />
              )}
              {landlordStatus === TransactionConstants.STATUS.PAST_DUE && (
                <Badge className="txtR fs-exclude" label="unpaid" type="fail" />
              )}
              {landlordStatus === TransactionConstants.STATUS.COMPLETE && (
                <Badge className="txtR fs-exclude" label="paid" type="success" />
              )}
              {showReturnedBadgeForPastDue && (
                <Badge className="txtR payments-returned-badge fs-exclude" label="returned" type="info" />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderExpandablePremiumCostsSection = (): React.ReactNode => {
    const { costItems } = props.rProTransaction;
    return (
      <>
        {costItems?.map((costItem, i) => (
          <Row className="pbn" key={i.toString()}>
            <Col xs={8} sm={8} md={10} lg={10} xl={10} xxl={10} className="paymentDetailsRow-Mobile">
              <Text className="fs-exclude">{costItem.description}</Text>
            </Col>
            <Col
              xs={4}
              sm={4}
              md={2}
              lg={2}
              xl={2}
              xxl={2}
              className="paymentDetailsRow-Mobile Transaction-expansion-price-padding txtR"
            >
              <Text type="body" className="fs-exclude">
                {centsToDollars(costItem.amount)}
              </Text>
            </Col>
          </Row>
        ))}
      </>
    );
  };

  const renderNonExpandablePremiumSection = (): React.ReactNode => {
    const managePolicyUrl = StringConstants.LINKS.RENTAL_PROTECTION.MANAGE_POLICY_PAGE(props.listingAlias);
    return (
      <Row className="pbn">
        <Col xs={8} sm={8} md={10} lg={10} xl={10} xxl={10} className="paymentDetailsRow-Mobile">
          <Label className="flexDisplay fs-exclude">
            To LeaseLock
            <HelpToolTip>
              Premiums will be processed along with your tenant&apos;s monthly rent payment. If the rent payment fails,
              the premium, plus any applicable state surplus lines taxes and fees, will be processed automatically and
              debited from your account 5 days after the rent due date.
            </HelpToolTip>
          </Label>
          <Text type="body" className="fs-exclude">
            <Link escape={false} href={managePolicyUrl} target="_blank">
              View Rental Protection Policy
            </Link>
          </Text>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <ExpansionPanel
        isExpanded={expandedRentalProtectionPremiumSection}
        onHeaderClick={toggleRentalProtectionPremium}
        header={renderExpansionHeader()}
        expandedContent={renderExpandablePremiumCostsSection()}
        className="Transaction-expansion-row"
      />
      {renderNonExpandablePremiumSection()}
    </>
  );
};

export default RentalProtectionPremiumRow;
